import React, { useState, useEffect } from 'react';
import '../css/BugReport.css';
import { FaRegUser, FaRegCircle, FaCheckCircle, FaLock, FaHourglassHalf, FaUserCheck } from "react-icons/fa";
import { bugReportsAPI } from '../api/api';

const statusIcon = (status) => {
  if (status === 'Solved' || status === 'Megoldva') return <FaCheckCircle style={{ color: '#4caf50', fontSize: 16 }} />;
  if (status === 'Lezárva') return <FaLock style={{ color: '#d32f2f', fontSize: 16 }} />;
  if (status === 'In progress' || status === 'Folyamatban') return <FaHourglassHalf style={{ color: '#2196f3', fontSize: 16 }} />;
  if (status === 'Waiting for answer' || status === 'Válaszra vár') return <FaUserCheck style={{ color: '#ff9800', fontSize: 16 }} />;
  return <FaRegCircle style={{ color: '#ff9800', fontSize: 16 }} />;
};

const statusTextClass = (status) => {
  if (status === 'Solved' || status === 'Megoldva') return 'bugreport-status-solved';
  if (status === 'Lezárva') return 'bugreport-status-closed';
  if (status === 'In progress' || status === 'Folyamatban') return 'bugreport-status-in-progress';
  if (status === 'Waiting for answer' || status === 'Válaszra vár') return 'bugreport-status-waiting';
  return 'bugreport-status-open';
};

const BugReportView = ({ ticket, onClose, onCloseTicket }) => {
  const [editorContent, setEditorContent] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load ticket messages when component mounts
  useEffect(() => {
    if (ticket && ticket.id) {
      loadTicketMessages();
    }
  }, [ticket?.id]);

  if (!ticket) return null;
  
  const isClosed = ticket.status === 'Lezárva' || ticket.status === 'Solved' || ticket.status === 'Megoldva';

  const loadTicketMessages = async () => {
    try {
      setLoading(true);
      // For now, we'll use the last message from the ticket
      // In a full implementation, you might want to add an API endpoint to get all messages
      if (ticket.lastMessage && ticket.lastMessage !== '-') {
        setMessages([{
          id: Date.now(),
          message: ticket.lastMessage,
          timestamp: new Date().toISOString(),
          isUser: false
        }]);
      }
    } catch (err) {
      console.error('Error loading ticket messages:', err);
      setError('Hiba az üzenetek betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!isClosed && editorContent && editorContent.trim().length > 0) {
      try {
        setLoading(true);
        setError('');
        
        const result = await bugReportsAPI.addBugReportMessage({
          ticket_id: ticket.id,
          message: editorContent.trim()
        });
        
        // Add the new message to the local state
        const newMessage = {
          id: result.id,
          message: result.message,
          timestamp: result.timestamp,
          isUser: true
        };
        
        setMessages(prev => [newMessage, ...prev]);
        setEditorContent('');
        
        // Update the ticket's last message
        if (onCloseTicket) {
          // This would ideally update the ticket status in the parent component
          // For now, we'll just clear the editor
        }
      } catch (err) {
        console.error('Error sending message:', err);
        setError('Hiba az üzenet küldésekor: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="bugreport-view-main bugreport-view-flex">
      <div className="bugreport-view-content">
        <div className="bugreport-view-title-row">
          <div className="bugreport-view-title">{ticket.subject}</div>
          <div className="bugreport-view-date">{ticket.createdAt}</div>
        </div>
        <div className="bugreport-view-message">{ticket.message}</div>
        
        {error && (
          <div className="bugreport-view-error" style={{ 
            background: '#ffebee', 
            color: '#c62828', 
            padding: '8px 12px', 
            margin: '12px 0', 
            borderRadius: '4px',
            border: '1px solid #ffcdd2',
            fontSize: '14px'
          }}>
            {error}
            <button 
              onClick={() => setError('')} 
              style={{ 
                float: 'right', 
                background: 'none', 
                border: 'none', 
                color: '#c62828', 
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ×
            </button>
          </div>
        )}
        
        <form className="bugreport-view-form" onSubmit={handleSend}>
          <div className="bugreport-view-draft-wrapper">
                         <label htmlFor="bugreport-message-textarea" className='bugreport-label'>Üzenet</label>
            <textarea
              id="bugreport-message-textarea"
              value={editorContent}
              onChange={e => {
                if (e.target.value.length <= 900) setEditorContent(e.target.value);
              }}
              className="bugreport-editor-wrapper"
              disabled={isClosed || loading}
              placeholder={loading ? "Küldés..." : "Írd be az üzeneted..."}
              rows={4}
              maxLength={900}
            />
                         <div className="bugreport-character-counter">{editorContent.length}/900 karakter</div>
          </div>
          <button type="submit" className="bugreport-view-send-btn" disabled={isClosed || !editorContent.trim() || loading}>
            {loading ? 'Küldés...' : 'Küldés'}
          </button>
        </form>
        
        <div className="bugreport-view-messages">
          {loading && messages.length === 0 ? (
            <div className="bugreport-view-messages-loading">Betöltés...</div>
          ) : messages.length === 0 ? (
            <div className="bugreport-view-messages-empty">Nincs üzenet.</div>
          ) : (
            messages.map((msg, index) => (
              <div key={msg.id || index} className={`bugreport-view-message-item ${msg.isUser ? 'user-message' : 'support-message'}`}>
                <div className="message-author">
                  {msg.isUser ? 'Te' : 'Támogatás'}
                </div>
                <div className="message-content">{msg.message}</div>
                <div className="message-timestamp">
                  {new Date(msg.timestamp).toLocaleString('hu-HU')}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      <div className="bugreport-view-side">
        <div className="bugreport-view-statusbox">
          <div className="bugreport-view-status-label">Hibajegy állapota:</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            {statusIcon(ticket.status)}
            <span className={`bugreport-view-status-text ${statusTextClass(ticket.status)}`}>{ticket.status}</span>
          </div>
          <div className="bugreport-view-type">{ticket.type}</div>
        </div>
        <div className="bugreport-view-userbox-side">
          <div className="bugreport-view-usericon"><FaRegUser style={{verticalAlign:'text-top'}} /></div>
          <div>
            <div className="bugreport-view-username">{ticket.userName}</div>
            <div className="bugreport-view-userrole">{ticket.userRole === 'boss' ? 'Admin' : 'Felhasználó'}</div>
          </div>
        </div>
        {!isClosed && ticket.status !== 'Solved' && ticket.status !== 'Megoldva' && (
          <button className="bugreport-view-close-btn" onClick={() => onCloseTicket(ticket.id)}>Lezárás</button>
        )}
        {isClosed && (
          <div className="bugreport-view-closed">Lezárva</div>
        )}
      </div>
      {onClose && (
        <button className="bugreport-view-x" onClick={onClose}>×</button>
      )}
    </div>
  );
};

export default BugReportView; 